<template>
  <div>
    <div id="appForm" class="mx-auto col-11 mt-5">
      <div class="flex flexLeft mx-auto contentsWidth">
        <p class="h4 text-center fw-3">{{year}}年度 {{name}} - 報告</p>
      </div>
      <hr class="title contentsWidth">

      <div class="notice mx-auto contentsWidth my-4 py-3 px-4">
        <p class="mb-1">・すべての必要項目に入力後、登録を行ってください。</p>
        <p class="mb-1">・数字は半角、カナは全角で入力してください。</p>
      </div>

      <b-tabs
        class="gas-tab mb-3 mx-auto contentsWidth"
        nav-wrapper-class="gas-tab-wrap">
        <b-tab
          v-if="viewControl && !isAgentView">
          <template v-slot:title>
            <span>報告情報</span>
          </template>
          <v-app-controlview
            :code="code"
            :status="status"
            :reason="reason"/>
        </b-tab>
        <b-tab v-if="!isAgentView">
          <template v-slot:title>
            <span>報告者情報</span>
            <img
              v-if="hasUserErr"
              class="errorMark"
              src="@/assets/svg/error.svg">
          </template>
          <v-app-userinfo
            :writable="otherWritable"
            :close_datetime="close_datetime"/>
        </b-tab>
        <b-tab
          @click="scrollBar(index)"
          v-for="(page, index) in visiblePages" :key="index">
          <template v-slot:title>
            <span>{{page.name}}</span>
            <img
              v-if="hasPageErr[index]"
              class="errorMark"
              src="@/assets/svg/error.svg">
          </template>
          <v-app-form
            :writable="otherWritable"
            :pageIndex="index"
            :assets="assets"
            :downloadFormFile="downloadFormFile"
            :downloadOpenFile="downloadOpenFile"/>
        </b-tab>
        <!-- No.7 採択後手続き（採択者側・常時表示）。
             ①②任意・③必須。テンプレDLは設定済みのみ（表示ルールB）。 -->
        <b-tab>
          <template v-slot:title>
            <span>採択者手続き</span>
            <img
              v-if="hasAdoptionErr"
              class="errorMark"
              src="@/assets/svg/error.svg">
          </template>
          <v-app-adoption
            :value="adoptionData"
            @input="v => adoptionData = v"
            :templates="adoptionTemplates"
            :writable="adoptionWritable"
            :errors="adoptionErrs"
            @select-file="onAdoptionSelectFile"
            @download-template="downloadAdoptionTemplate"
            @download-uploaded="downloadAdoptionUploaded"
            @download-other="downloadAdoptionOther"/>
        </b-tab>
        <!-- No.23 複数回アンケート（見せる回が1つ以上あるときのみ表示）。回ごとにセクション表示。 -->
        <b-tab v-if="hasAnyRound && !isAgentView">
          <template v-slot:title>
            <span>アンケート</span>
            <img
              v-if="hasSurveyErr"
              class="errorMark"
              src="@/assets/svg/error.svg">
          </template>
          <!-- 全回を1つの枠（他タブと同じ ContentsBox）に入れ、スクロールもタブ内で完結させる -->
          <perfect-scrollbar
            :options="scrollOption"
            class="py-2 px-2 ContentsBox">
            <div
              v-for="round in surveyRounds" :key="round.id"
              class="survey-round mb-4">
              <div class="survey-round-head px-3 py-2 mb-2">
                <span class="bold mr-2">{{ round.name }}</span>
                <span v-if="round.state === 'open'" class="badge badge-primary">回答受付中</span>
                <span v-else class="badge badge-secondary">回答期間終了（閲覧のみ）</span>
                <span class="ml-2 text-muted small">{{ round.open_datetime }} 〜 {{ round.close_datetime }}</span>
              </div>
              <v-app-survey
                :surveys="round.surveys"
                :value="surveyAnswersByRound[round.id] || {}"
                @input="v => onSurveyRoundInput(round.id, v)"
                :writable="round.state === 'open'"
                :errors="surveyErrsByRound[round.id] || {}"
                :framed="false"/>
            </div>
          </perfect-scrollbar>
        </b-tab>
      </b-tabs>

      <div class="bottonArea mt-4 mb-3 flex flexCenter">
        <p
          v-show="hasEmpty && !isRegisterOnly && !isAgentView"
          class="warning">
          未入力の必須項目があります。すべての必須項目に入力後、{{textIsEdit}}を行ってください。
        </p>
      </div>

      <div class="mt-4 mb-3 flex flexCenter contentsWidth">
        <b-button class="btn btn-lg mr-4 bold" :to="isAgentView ? '/agent/top' : '/top'">
          {{ isAgentView ? '代行者ページに戻る' : 'トップに戻る' }}
        </b-button>
        <!-- 一時保存：報告期間内は未提出時のみ／登録モードは常時（No.23 §19＝書きかけを保存できる手段） -->
        <b-button
          v-if="((onTime && isUnsubmited) || isRegisterOnly) && !isAgentView"
          @click="tempSave('temp')"
          variant="primary"
          class="btn btn-lg mr-4 bold"
          >一時保存</b-button>
        <!-- 登録：登録モードのみ。必須（開いている回のアンケート／A 中の採択者手続き③）を検証して確定する。
             No.7 Phase N：代行者は入力可（agentWritable）のときだけ表示する（canRegister）。 -->
        <b-button
          v-if="canRegister"
          @click="isAgentView ? saveAdoptionByAgent() : tempSave('register')"
          variant="primary"
          class="btn btn-lg mr-4 bold"
          >登録</b-button>
        <!-- 本提出は報告期間のみ。登録モード（isRegisterOnly）は登録に限定（本提出なし） -->
        <!-- No.7 Phase J：報告本体のみ検証／全項目（採択者手続き③・アンケート含む）検証の2本立て -->
        <b-button
          v-if="canSend && !isRegisterOnly && !isAgentView"
          @click="save('report')"
          variant="primary"
          class="bold mr-4"
          >{{textIsEdit}}</b-button>
        <b-button
          v-if="canSend && !isRegisterOnly && !isAgentView"
          @click="save('all')"
          variant="primary"
          class="bold mr-4"
          >{{textIsEdit}}（全項目）</b-button>
        <b-button
          v-if="onTime && isSubmited && !isAgentView"
          @click="withdraw"
          variant="dark"
          class="btn btn-primary btn-lg bold"
          >報告の削除</b-button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/modules/api';
import download from '@/modules/download';
import Userinfo from '@/components/parts/application/Userinfo.vue';
import AppForm from '@/components/gas/parts/Form.vue';
import AppControlview from '@/components/gas/parts/Controlview.vue';
import Survey from '@/components/parts/report/Survey.vue';
import AdoptionProcedure from '@/components/parts/report/AdoptionProcedure.vue';
import CONST_STATUS from '@/constants/appStatus';


export default {
  name: 'gasApplicationForm',
  components: {
    'v-app-userinfo': Userinfo,
    'v-app-form': AppForm,
    'v-app-controlview': AppControlview,
    'v-app-survey': Survey,
    'v-app-adoption': AdoptionProcedure,
  },
  data() {
    return {
      isEdit: false,
      appId: null,
      reptypeId: null,
      year: null,
      name: '',
      code: null,
      reason: '',
      status: null,
      hasUserErr: false,
      hasPageErr: [],
      initTabChange: {},
      onTime: null,
      viewLocateMsg: true,
      formStatus: null,
      // No.23 Phase 3：登録モード（報告期間外だが採択者手続き A またはアンケート回答期間 S が開いている）。
      isRegisterOnly: false,
      // 採択者手続きタブの入力可否（A＝採択者手続き期間中）。#7（S のみ）では false。
      adoptionOnTime: false,
      assets: {},
      close_datetime: null,
      pageErrs: [],
      // No.23 複数回アンケート：回ごと（[{id,name,state,open_datetime,close_datetime,surveys,answers}]）
      surveyRounds: [],
      // 回ごとの回答マップ {roundId: {surveyId: {answer_text, option_ids}}}
      surveyAnswersByRound: {},
      // 回ごとの必須エラー {roundId: {surveyId: message}}
      surveyErrsByRound: {},
      hasSurveyErr: false,
      // アンケートタブの外枠スクロール（全回をまとめて1つのスクロールにする）
      scrollOption: {
        wheelPropagation: true,
        suppressScrollX: true,
      },
      // No.7 採択後手続き
      adoptionData: this.initAdoptionData(null),
      adoptionTemplates: { donation: null, exemption: null },
      // No.9 (B)：代行者の入力可否（サーバが R または A で判定して返す）
      agentWritable: false,
      adoptionErrs: {},
      hasAdoptionErr: false,
      adoptionFiles: { donation: null, exemption: null },
    };
  },
  computed: {
    pages() {
      return this.$store.state.gasApplicationForm.pages;
    },
    hasEmpty() {
      // ユーザー情報
      const userInfo = this.$store.state.userInfo.inputData;
      const userInfoForm = this.$store.state.userInfoForm.form;
      const findUserResult = Object.keys(userInfo).find((key) => {
        if (!userInfoForm[key].is_required) {
          return false;
        }
        return userInfo[key] === '' || userInfo[key] === null;
      });

      // 入力フォーム
      const { inputs } = this.$store.state.gasApplicationInput;
      const formOptionData = this.$store.state.gasApplicationForm.optionDataList;
      const findFormResult = inputs.find((input) => {
        if (formOptionData[input.option_id] && !formOptionData[input.option_id].is_required) {
          return false;
        }
        const noSelect = input.field_answer_id === null;
        const notext = input.field_answer_text === '';
        const noFile = input.uploadedFileKey === '';
        return noSelect && notext && noFile;
      });
      return typeof findUserResult !== 'undefined' || typeof findFormResult !== 'undefined';
    },
    isUnsubmited() {
      return this.status === null || this.status === CONST_STATUS.APP_STATUS.TEMPSAVE;
    },
    isSubmited() {
      return this.status === CONST_STATUS.APP_STATUS.SUBMIT;
    },
    viewControl() {
      const viewControlStatusList = CONST_STATUS.GAS_VIEW_CONTROL_STATUS;
      return viewControlStatusList.indexOf(this.status) !== -1;
    },
    formStatuses() {
      return CONST_STATUS.FROM_STATUS;
    },
    canSend() {
      const isInit = this.formStatus === this.formStatuses.INIT;
      const isEdit = this.formStatus === this.formStatuses.EDIT;
      return isInit || isEdit;
    },
    // 登録モード（報告期間外）の間は、報告本体・報告者情報タブは表示のみ
    // No.9 (B)：代行者の表示（採択者手続きタブのみ・登録ボタンのみ）。
    // 代行者専用ルート（/agent/report/form/:reptypeId）で開かれたかで判定する。
    isAgentView() {
      return this.$route.name === 'agentReportForm';
    },
    // ダウンロードの呼び先（代行者は /api/agent/report/*）
    reportApiBase() {
      return this.isAgentView ? '/api/agent/report' : '/api/report';
    },
    // 代行者には報告本体のページを見せない（v-if と v-for の併用を避けるため配列側で絞る）
    visiblePages() {
      return this.isAgentView ? [] : this.pages;
    },
    otherWritable() {
      return this.canSend && !this.isRegisterOnly;
    },
    // No.7 Phase N：「登録」ボタンの表示条件。
    // 代行者は入力可（agentWritable＝採択者手続き期間中）のときだけ登録できる。
    // 閲覧のみの状態では保存API（Agent/Report@saveAdoption）が 403 を返すため、
    // 「押せるのに必ず失敗するボタン」を出さない。申請者は従来どおり登録モードのみ。
    canRegister() {
      if (this.isAgentView) {
        return this.agentWritable;
      }
      return this.isRegisterOnly;
    },
    // 採択者手続きタブは A（採択者手続き期間中）かつ編集可のとき入力可
    adoptionWritable() {
      if (this.isAgentView) {
        return this.agentWritable;
      }
      return this.canSend && this.adoptionOnTime;
    },
    // アンケートタブの表示（見せる回が1つ以上あるか＝OPEN/CLOSED）
    hasAnyRound() {
      return this.surveyRounds.length > 0;
    },
    hasChange() {
      return this.$store.state.gasApplicationForm.hasChange;
    },
    textIsEdit() {
      if (!this.isEdit) {
        return '報告';
      }
      return '再報告';
    },
  },
  methods: {
    /**
     * No.9 (B)：代行者用のデータ取得。
     * 採択者手続きに必要な最小限だけを1回で取る（報告本体・報告者情報・アンケートは取得しない）。
     */
    async fetchAgentData(reptypeId) {
      const res = await api.send('/api/agent/report/form', { reptypeId }).catch(() => false);
      if (res === false) {
        await this.alert('データを取得できませんでした。');
        this.$router.push({ name: 'agentTop' });
        return;
      }
      const d = res.data;
      this.year = d.year;
      this.name = d.name;
      this.agentWritable = !!d.writable;
      this.adoptionData = this.initAdoptionData(d.adoption);
      this.adoptionTemplates = d.adoptionTemplates || { donation: null, exemption: null };
    },
    /**
     * No.9 (B)：代行者の保存（採択者手続きのみ）。
     * ★報告本体・アンケートは送らない＝サーバも saveAdoptionOnly しか呼ばない。
     */
    async saveAdoptionByAgent() {
      this.adoptionErrs = {};
      this.hasAdoptionErr = false;
      if (!await this.confirm('現在の内容で登録してもよろしいですか？')) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const storeData = {
        reptypeId: this.reptypeId,
        adoptionProcedure: JSON.stringify(this.toAdoptionPayload()),
      };
      this.attachAdoptionFiles(storeData);
      const response = await api.sendForm('/api/agent/report/saveAdoption', storeData)
        .catch(async (err) => {
          if (err.response.status === 422) {
            await this.alert('入力内容に誤りがありました。修正してください。', false);
            this.setAdoptionErrs(err.response.data.adoptionErrs);
          }
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        return;
      }
      await this.fetchAgentData(this.reptypeId);
      await this.alert('登録しました。', false);
    },
    async fetchApptypeData(reptypeId) {
      const param = {
        reptypeId,
      };
      const promiseFuncs = [
        api.send('/api/report/base', param),
        api.send('/api/report/myrep', param),
        api.send('/api/report/form', param),
        api.send('/api/report/edit', param),
      ];
      const responses = await api.all(promiseFuncs)
        .catch((err) => {
          console.error(err);
        });
      if (!responses) {
        await this.alert('データを取得できませんでした。');
        this.$router.push({ name: 'gasTop' });
        return;
      }
      const { base, onTime, appId } = responses[0].data;
      this.appId = appId;
      this.year = base.app_type.year;
      this.name = base.app_type.name;
      this.onTime = onTime;
      this.assets = base.assets;
      this.close_datetime = base.close_datetime;
      const { myrep, formStatus, isRegisterOnly, adoptionOnTime } = responses[1].data;
      this.formStatus = formStatus;
      // No.23 Phase 3：登録モード（報告期間外だが A または S が開いている）
      this.isRegisterOnly = !!isRegisterOnly;
      this.adoptionOnTime = !!adoptionOnTime;
      if (myrep !== null) {
        this.code = myrep.code;
        this.reason = myrep.reason;
        this.status = myrep.status;
        // 一時保存の時はデータがあっても編集ではない
        this.isEdit = this.status !== CONST_STATUS.APP_STATUS.TEMPSAVE;
      }
      const { pages } = responses[2].data;
      const {
        userInfo, answers, reportId, surveyRounds, adoption, adoptionTemplates,
      } = responses[3].data;
      // No.23 複数回アンケート（回ごとに設問＋この採択者本人の回答）
      this.surveyRounds = surveyRounds || [];
      const answersByRound = {};
      this.surveyRounds.forEach((round) => {
        answersByRound[round.id] = this.initSurveyAnswers(round.surveys, round.answers);
      });
      this.surveyAnswersByRound = answersByRound;
      // No.7 採択後手続き（提出データ＋テンプレ〔name|null〕）
      this.adoptionData = this.initAdoptionData(adoption);
      this.adoptionTemplates = adoptionTemplates || { donation: null, exemption: null };
      this.$store.commit('gasApplicationForm/setDbData', pages);
      this.$store.commit('gasApplicationForm/setReportId', reportId);
      this.$store.commit('gasApplicationInput/setDbData', { pages, answers });
      this.$store.commit('userInfo/setDbData', userInfo);
    },
    // 既存回答（行）を回答マップ {surveyId:{answer_text, option_ids}} に変換
    initSurveyAnswers(surveys, answerRows) {
      const map = {};
      (surveys || []).forEach((s) => {
        map[s.id] = { answer_text: '', option_ids: [] };
      });
      (answerRows || []).forEach((r) => {
        if (!map[r.survey_id]) {
          map[r.survey_id] = { answer_text: '', option_ids: [] };
        }
        if (r.answer_text !== null && r.answer_text !== undefined && r.answer_text !== '') {
          map[r.survey_id].answer_text = r.answer_text;
        }
        if (r.option_id !== null && r.option_id !== undefined) {
          map[r.survey_id].option_ids.push(r.option_id);
        }
      });
      return map;
    },
    // 回ごとの回答入力を反映
    onSurveyRoundInput(roundId, v) {
      this.$set(this.surveyAnswersByRound, roundId, v);
    },
    // 回ごとの回答マップ → 送信用（開いている回=OPEN のみ）[{survey_round_id, answers:[{survey_id, answer_text, option_ids}]}]
    toSurveyPayload() {
      return this.surveyRounds
        .filter(round => round.state === 'open')
        .map((round) => {
          const map = this.surveyAnswersByRound[round.id] || {};
          const answers = (round.surveys || []).map((s) => {
            const a = map[s.id] || { answer_text: '', option_ids: [] };
            return {
              survey_id: s.id,
              answer_text: a.answer_text || '',
              option_ids: a.option_ids || [],
            };
          });
          // No.23 §22：開いた時点の版を送り返す（間に別の画面が更新していないかをサーバで見る）
          return { survey_round_id: round.id, answer_version: round.answer_version, answers };
        });
    },
    // No.23 §22：保存後の版で画面の持ち物を更新する。
    // 一時保存は画面に留まるため、これをしないと続けて保存したときに
    // 自分自身の保存を「他の画面の更新」と誤検知して弾かれる。
    applySurveyVersions(versions) {
      if (!versions || versions.length === 0) {
        return;
      }
      const byRound = {};
      versions.forEach((v) => { byRound[v.survey_round_id] = v.answer_version; });
      this.surveyRounds.forEach((round, i) => {
        if (byRound[round.id] !== undefined) {
          this.$set(this.surveyRounds[i], 'answer_version', byRound[round.id]);
        }
      });
    },
    // No.23 §22：版ズレの案内。出したら true を返す（呼び出し側が他のエラー表示を打ち切る）。
    // 入力の誤りではないので「入力内容に誤りがありました」ではなく再読込を促す。
    async setSurveyConflicts(conflicts) {
      if (!conflicts || conflicts.length === 0) {
        return false;
      }
      await this.alert(conflicts.map(c => c.message).join('\n'), false);
      return true;
    },
    // 必須エラー配列 [{survey_id, message}] → 回ごとのマップ＋タブのエラーマーク
    setSurveyErrs(surveyErrs) {
      // survey_id → roundId の逆引き
      const roundOf = {};
      this.surveyRounds.forEach((round) => {
        (round.surveys || []).forEach((s) => { roundOf[s.id] = round.id; });
      });
      const byRound = {};
      let count = 0;
      (surveyErrs || []).forEach((e) => {
        const rid = roundOf[e.survey_id];
        if (rid === undefined) { return; }
        if (!byRound[rid]) { byRound[rid] = {}; }
        byRound[rid][e.survey_id] = e.message;
        count += 1;
      });
      this.surveyErrsByRound = byRound;
      this.hasSurveyErr = count > 0;
    },
    // No.7：サーバーの採択後手続きデータ → コンポーネント value 形へ
    initAdoptionData(adoption) {
      const a = adoption || {};
      return {
        addressee: a.addressee || '',
        postalCode: a.postal_code || '',
        address: a.address || '',
        tel: a.tel || '',
        fax: a.fax || '',
        donation: { name: a.donation_name || '', submitted: !!a.donation_name, delete: false },
        exemption: { name: a.exemption_name || '', submitted: !!a.exemption_name, delete: false },
        // その他添付（複数）：既存DB＝otherFiles[{id,name}]／新規＝otherNew／削除id＝otherDeleteIds
        otherFiles: a.otherFiles || [],
        otherNew: [],
        otherDeleteIds: [],
      };
    },
    // No.7：value → 送信用（住所＋①②の削除フラグ＋その他添付の削除id。実ファイルは別枠）
    toAdoptionPayload() {
      const d = this.adoptionData;
      return {
        addressee: d.addressee,
        postal_code: d.postalCode,
        address: d.address,
        tel: d.tel,
        fax: d.fax,
        donation: { delete: d.donation.delete },
        exemption: { delete: d.exemption.delete },
        otherDeleteIds: d.otherDeleteIds || [],
      };
    },
    // No.7：③必須エラー（サーバー422 adoptionErrs）→ コンポーネント errors＋タブマーク
    setAdoptionErrs(adoptionErrs) {
      this.adoptionErrs = adoptionErrs || {};
      this.hasAdoptionErr = Object.keys(this.adoptionErrs).length > 0;
    },
    // No.7：①②のファイル選択/削除（File を保持・null で選択解除）
    onAdoptionSelectFile({ key, file }) {
      this.adoptionFiles[key] = file;
    },
    // No.7：選択済みの①②＋その他添付ファイルを multipart に添付
    // ①②＝real_adoptionDonation/Exemption／その他＝real_adoptionOther[i]（Laravelで配列受け）
    attachAdoptionFiles(storeData) {
      const data = storeData;
      if (this.adoptionFiles.donation) {
        data.real_adoptionDonation = this.adoptionFiles.donation;
      }
      if (this.adoptionFiles.exemption) {
        data.real_adoptionExemption = this.adoptionFiles.exemption;
      }
      (this.adoptionData.otherNew || []).forEach((o, i) => {
        data[`real_adoptionOther[${i}]`] = o.file;
      });
    },
    // No.7：①②テンプレDL（fileType=4/5）
    async downloadAdoptionTemplate(fileType) {
      const param = { reptypeId: this.reptypeId, fileType };
      this.$store.dispatch('page/onWaiting');
      const response = await api.send(`${this.reportApiBase}/adoption/download/template`, param, true)
        .catch((err) => { console.error(err); });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('フォーマットのダウンロードに失敗しました。');
        return;
      }
      const key = fileType === 4 ? 'donation' : 'exemption';
      const tpl = this.adoptionTemplates[key];
      download.blob(response.data, (tpl && tpl.name) || 'format');
    },
    // No.7：提出済みの①②を再DL（key=donation/exemption）
    async downloadAdoptionUploaded(key) {
      const param = { reptypeId: this.reptypeId, fileKey: key };
      this.$store.dispatch('page/onWaiting');
      const response = await api.send(`${this.reportApiBase}/adoption/download/uploaded`, param, true)
        .catch((err) => { console.error(err); });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, this.adoptionData[key].name || 'file');
    },
    // No.7 H-3：その他添付（既存DBファイル）を id 指定で再DL
    async downloadAdoptionOther(id) {
      const target = (this.adoptionData.otherFiles || []).find(f => f.id === id);
      const param = { reptypeId: this.reptypeId, fileId: id };
      this.$store.dispatch('page/onWaiting');
      const response = await api.send(`${this.reportApiBase}/adoption/download/other`, param, true)
        .catch((err) => { console.error(err); });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, (target && target.name) || 'file');
    },
    // No.23 §19：scope='temp'＝一時保存（必須を検証しない書きかけ保存）／'register'＝登録（必須を検証）。
    // 既定は 'temp'＝呼び出し漏れでも採択者が弾かれない方に倒す（サーバ側は逆に検証ありへ倒すため穴は開かない）。
    async tempSave(scope = 'temp') {
      const isRegister = scope === 'register';
      this.pageErrs = [];
      this.surveyErrsByRound = {};
      this.hasSurveyErr = false;
      this.adoptionErrs = {};
      this.hasAdoptionErr = false;
      const confirmMsg = isRegister
        ? '現在の内容で登録してもよろしいですか？'
        : '現在の内容で一時保存をしてもよろしいですか？';
      if (!await this.confirm(confirmMsg)) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const storeData = {
        appId: this.appId,
        reptypeId: this.reptypeId,
        userInfo: JSON.stringify(this.$store.state.userInfo.inputData),
        inputs: JSON.stringify(this.$store.state.gasApplicationInput.inputs),
        surveyAnswers: JSON.stringify(this.toSurveyPayload()),
        adoptionProcedure: JSON.stringify(this.toAdoptionPayload()),
        // No.23 §19：'temp'（一時保存）だけ必須検証をスキップする
        registerScope: scope,
      };
      const { files } = this.$store.state.gasApplicationInput;
      Object.keys(files).forEach((key) => {
        storeData[key] = files[key];
      });
      this.attachAdoptionFiles(storeData);
      const response = await api.sendForm('/api/report/tempSave', storeData)
        .catch(async (err) => {
          // zz06：422 以外（500 等）や応答なし（通信断）を握りつぶさない。
          // 何も出さないと「押しても反応しない」だけが残り、原因の手がかりが画面に残らない。
          // err.response が undefined のとき、従来はこの catch 内で TypeError になっていた。
          if (!err.response || err.response.status !== 422) {
            await this.alert('保存に失敗しました。時間をおいて再度お試しください。');
            return false;
          }
          // No.23 §22：版ズレ（他の画面での更新）は入力の誤りではないため先に案内して打ち切る
          if (await this.setSurveyConflicts(err.response.data.surveyConflicts)) {
            return false;
          }
          await this.alert('入力内容に誤りがありました。修正してください。', false);
          this.$store.commit('gasApplicationInput/setErrMessages', err.response.data.formErrs);
          this.$store.commit('userInfo/setErrorMsg', err.response.data.userErrs);
          this.setErrorMarks(err.response.data.userErrs);
          // No.23 §18：登録モードのアンケート必須エラー（タブのエラーマークも立つ）
          this.setSurveyErrs(err.response.data.surveyErrs);
          // No.23 §19：採択者手続き③（A 中の「登録」のみ）
          this.setAdoptionErrs(err.response.data.adoptionErrs);
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        return;
      }
      this.$store.commit('userInfo/resetErrorMsg');
      this.$store.commit('gasApplicationInput/initErrMessages');
      this.$store.commit('gasApplicationForm/setHasChange', false);
      this.hasUserErr = false;
      this.hasPageErr = [];
      // No.23 §22：この画面のまま続けて保存できるよう、保存後の版に更新する
      this.applySurveyVersions(response.data.surveyVersions);
      await this.alert(isRegister ? '登録しました。' : '一時保存しました。');
    },
    // No.7 Phase J：scope='report'＝報告本体のみ検証／'all'＝採択者手続き③・アンケートも検証。
    // 確認文言は両ボタン共通（scope で分岐しない）。入力データは scope 不問で全保存される。
    async save(scope) {
      if (!await this.confirm('現在の内容で報告をしてもよろしいですか？')) {
        return;
      }
      this.pageErrs = [];
      this.surveyErrsByRound = {};
      this.hasSurveyErr = false;
      this.adoptionErrs = {};
      this.hasAdoptionErr = false;
      this.$store.dispatch('page/onWaiting');
      const storeData = {
        appId: this.appId,
        reptypeId: this.reptypeId,
        userInfo: JSON.stringify(this.$store.state.userInfo.inputData),
        inputs: JSON.stringify(this.$store.state.gasApplicationInput.inputs),
        surveyAnswers: JSON.stringify(this.toSurveyPayload()),
        adoptionProcedure: JSON.stringify(this.toAdoptionPayload()),
        submitScope: scope,
      };
      const { files } = this.$store.state.gasApplicationInput;
      Object.keys(files).forEach((key) => {
        storeData[key] = files[key];
      });
      this.attachAdoptionFiles(storeData);
      const response = await api.sendForm('/api/report/save', storeData)
        .catch(async (err) => {
          // zz06：422 以外（500 等）や応答なし（通信断）を握りつぶさない。
          // 何も出さないと「押しても反応しない」だけが残り、原因の手がかりが画面に残らない。
          // err.response が undefined のとき、従来はこの catch 内で TypeError になっていた。
          if (!err.response || err.response.status !== 422) {
            await this.alert('保存に失敗しました。時間をおいて再度お試しください。');
            return false;
          }
          // No.23 §22：版ズレ（他の画面での更新）は入力の誤りではないため先に案内して打ち切る
          if (await this.setSurveyConflicts(err.response.data.surveyConflicts)) {
            return false;
          }
          await this.alert('入力内容に誤りがありました。修正してください。', false);
          this.$store.commit('gasApplicationInput/setErrMessages', err.response.data.formErrs);
          this.$store.commit('userInfo/setErrorMsg', err.response.data.userErrs);
          this.pageErrs = err.response.data.pageErrs;
          this.setErrorMarks(err.response.data.userErrs);
          this.setSurveyErrs(err.response.data.surveyErrs);
          this.setAdoptionErrs(err.response.data.adoptionErrs);
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        return;
      }
      this.$store.commit('gasApplicationInput/setCode', response.data.code);
      // リダイレクト
      this.$router.push({ name: 'gasReportEnd', meta: { locate: false } });
    },
    async downloadOpenFile(assetId, assetName) {
      const param = {
        reptypeId: this.reptypeId,
        assetId,
      };
      this.$store.dispatch('page/onWaiting');
      const requireBlob = true;
      const response = await api.send('/api/report/download/file/form', param, requireBlob)
        .catch((err) => {
          console.error(err);
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, assetName);
    },
    async downloadFormFile(fieldId, optionId, fileName) {
      const param = {
        report_id: this.$store.state.gasApplicationForm.report_id,
        field_id: fieldId,
        option_id: optionId,
        answer_no: 1, // ファイルは当面１つ固定
      };
      const url = '/api/report/download/uploadedFile';
      this.$store.dispatch('page/onWaiting');
      const response = await api.send(url, param, true)
        .catch((err) => {
          console.error(err);
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        await this.alert('ファイルのダウンロードに失敗しました。');
        return;
      }
      download.blob(response.data, fileName);
    },
    setErrorMarks(userErrs) {
      // ユーザー
      this.hasUserErr = Object.keys(userErrs).length > 0;
      // フォーム
      const { inputs } = this.$store.state.gasApplicationInput;
      Object.keys(this.pages).forEach((index) => {
        const { fields } = this.pages[index];
        const thisPageFieldIdList = Object.keys(fields).map((fieldKey) => {
          return fields[fieldKey].field_id;
        });
        let findFormErr = inputs.find((input) => {
          const searchedIndex = thisPageFieldIdList.indexOf(input.field_id);
          const hasErr = input.errMessages.length > 0;
          return searchedIndex !== -1 && hasErr;
        });
        // 例外的にページにエラーマークをつけたいとき
        if (this.pageErrs.includes(Number(index))) {
          findFormErr = true;
        }
        this.hasPageErr.splice(index, 1, findFormErr);
      });
    },
    async withdraw() {
      if (!await this.confirm('入力内容はすべて破棄されます。この報告を削除してもよろしいですか？')) {
        return;
      }
      this.$store.dispatch('page/onWaiting');
      const param = {
        reptypeId: this.reptypeId,
      };
      const response = await api.send('/api/report/withdraw', param)
        .catch(async (err) => {
          if (err.response.status === 422) {
            await this.alert('入力内容に誤りがありました。修正してください。', false);
            this.$store.commit('gasApplicationInput/setErrMessages', err.response.data.formErrs);
            this.$store.commit('userInfo/setErrorMsg', err.response.data.userErrs);
            this.setErrorMarks(err.response.data.userErrs);
          }
          return false;
        });
      this.$store.dispatch('page/offWaiting');
      if (!response) {
        return;
      }
      await this.alert('報告を削除しました。');
      // トップへリダイレクト
      this.viewLocateMsg = false;
      this.$router.push({ name: 'gasTop' });
    },
    // ページ上部にスクロールし、スクロールバーを表示する。settimeoutがないと効かない。scrollUserinfoもなぜか効かない
    // createに書いてもだめ
    scrollBar(index) {
      if (!this.initTabChange[index]) {
        setTimeout(() => {
          document.querySelector('#scrollUserinfo').scrollTop = 1;
          Object.keys(this.pages).forEach((page) => {
            document.querySelector(`#scroll${page}`).scrollTop = 1;
          });
        }, 1);
        this.$set(this.initTabChange, index, true);
      }
    },
  },
  async beforeRouteLeave(to, from, next) {
    if (!this.hasChange || to.name === 'gasReportEnd' || this.viewLocateMsg === false) {
      next();
    } else if (await this.confirm('入力内容は破棄されます。このページから移動してもよろしいですか？')) {
      next();
    } else {
      next(false);
    }
  },
  // ロード画面
  async created() {
    this.$store.dispatch('page/onLoading');
    this.$store.commit('gasApplicationForm/init');
    this.$store.commit('userInfo/resetErrorMsg');
    this.$store.commit('gasApplicationForm/setIsCms', false);
    const { reptypeId } = this.$route.params;
    this.reptypeId = reptypeId;
    this.$store.commit('gasApplicationForm/setReportTypeId', reptypeId);
    if (this.isAgentView) {
      await this.fetchAgentData(reptypeId);
    } else {
      await this.fetchApptypeData(reptypeId);
    }
    this.$store.dispatch('page/offLoading');

    setTimeout(() => {
      document.querySelector('#scrollUserinfo').scrollTop = 1;
    }, 0);
  },
};
</script>

<style scoped>
  .notice {
    background-color: #F3F3F3;
    height: 80px;
  }

  .bottonArea {
    width: 1100px;
  }
  .errorMark {
    position: absolute;
    top: -7px;
    left: 150px;
  }
  .warning {
    color: #dc3545;
  }
  #appForm {
    display: block;
  }
  /*
    No.23 複数回アンケート：回の見出し。
    外枠（ContentsBox）はタブ直下に1つだけ置き、回ごとの見出しはその中の区切り帯にする。
    回ごとに枠を持たせると、2回目以降がタブから切り離された別の箱に見えてしまうため。
  */
  .survey-round-head {
    background-color: #F3F3F3;
    border: solid 1px #CCC;
  }
</style>
